import { useEffect, useState } from 'react';

import { HandlersPresenceBootstrapResponseFromJSON } from '~/api/models/HandlersPresenceBootstrapResponse';
import type { BootstrapResponse } from '~/marketing/presenceContract';

import { isJsonContentType } from '../utils/upstreamHttpResponse';

function apiBase(): string {
  return import.meta.env.VITE_PRESENCE_API_BASE_URL?.replace(/\/+$/, '') || '';
}

/**
 * Regenerated API: `GET /api/presence/bootstrap?city=` only. Stable `anonId` is from Set-Cookie / server session.
 */
export function presenceBootstrapUrl(city: string): string {
  const u = new URL(`${apiBase()}/api/presence/bootstrap`);
  u.searchParams.set('city', city);
  return u.toString();
}

type Status = 'idle' | 'loading' | 'ok' | 'error';

const presenceFetchInit: RequestInit = {
  credentials: 'include',
  headers: { Accept: 'application/json' },
};

export function usePresenceBootstrap(options: { enabled: boolean; city: string }) {
  const { enabled, city } = options;
  const [data, setData] = useState<BootstrapResponse | null>(null);
  const [status, setStatus] = useState<Status>('idle');

  useEffect(() => {
    if (!enabled || !apiBase()) {
      setData(null);
      setStatus('idle');
      return;
    }
    const ac = new AbortController();
    setStatus('loading');
    const url = presenceBootstrapUrl(city);
    void fetch(url, {
      ...presenceFetchInit,
      signal: ac.signal,
    })
      .then((r) => {
        if (!r.ok || !isJsonContentType(r.headers.get('content-type'))) {
          throw new Error(String(r.status));
        }
        return r.json();
      })
      .then((json: unknown) => {
        setData(HandlersPresenceBootstrapResponseFromJSON(json));
        setStatus('ok');
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        setData(null);
        setStatus('error');
      });
    return () => ac.abort();
  }, [enabled, city]);

  return { bootstrap: data, status };
}
